import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Bell, Loader2, AlertCircle } from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useApi } from "../../hooks/useApi";

function formatRelativeTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function sectionLabelForDate(isoString) {
  if (!isoString) return "Recent";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "Recent";
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfThatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfThatDay) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return "This week";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function severityStyles(severity) {
  const s = String(severity || "").toLowerCase();
  if (s === "high" || s === "critical") {
    return {
      ring: "ring-red-500/30",
      action: "bg-red-500 text-white hover:bg-red-600",
    };
  }
  if (s === "medium") {
    return {
      ring: "ring-amber-500/30",
      action: "bg-amber-500 text-white hover:bg-amber-600",
    };
  }
  return {
    ring: "ring-emerald-500/20",
    action: "bg-muted text-foreground hover:bg-muted",
  };
}

function logoFromItem(item) {
  const title = item?.title || item?.message || "?";
  const ch = String(title).trim().charAt(0).toUpperCase();
  const iconType = String(item?.icon_type || "info").toLowerCase();
  if (iconType === "warning") return { letter: ch, className: "bg-amber-500 text-white" };
  if (iconType === "error" || iconType === "alert") return { letter: ch, className: "bg-red-600 text-white" };
  return { letter: ch, className: "bg-slate-700 text-white dark:bg-slate-600" };
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { get } = useApi();

  const deviceIdFromQuery = searchParams.get("deviceId");
  const deviceIdFromState = location.state?.device_id;
  const deviceId = deviceIdFromQuery || deviceIdFromState || null;
  const vehicleLabel =
    location.state?.vehicle_number ||
    location.state?.vehicle_name ||
    (deviceId ? `${deviceId.slice(0, 8)}…` : null);

  const [search, setSearch] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [readIds, setReadIds] = useState(() => new Set());
  const touchStateRef = useRef({ id: null, startX: 0 });

  const fetchPage = useCallback(
    async (pageNum, append) => {
      if (!deviceId) return;
      setLoading(true);
      setLoadError(null);
      try {
        const res = await get(
          `/notifications/device/${encodeURIComponent(deviceId)}?page=${pageNum}&per_page=20`
        );
        const payload = res?.data ?? res;
        const list = Array.isArray(payload?.data) ? payload.data : [];
        const lp = payload?.last_page ?? 1;
        const tot = payload?.total ?? list.length;
        setLastPage(lp);
        setTotal(tot);
        setPage(pageNum);
        setNotifications((prev) => (append ? [...prev, ...list] : list));
      } catch (e) {
        setLoadError(e?.message || "Failed to load notifications");
        if (!append) setNotifications([]);
      } finally {
        setLoading(false);
      }
    },
    [deviceId, get]
  );

  useEffect(() => {
    if (!deviceId) {
      setNotifications([]);
      setLoadError(null);
      return;
    }
    fetchPage(1, false);
  }, [deviceId, fetchPage]);

  const onTouchStart = (id, e) => {
    touchStateRef.current = { id, startX: e.touches[0].clientX };
  };

  const onTouchEnd = (id, e) => {
    const { id: touchedId, startX } = touchStateRef.current;
    if (touchedId !== id) return;
    const endX = e.changedTouches[0].clientX;
    const deltaX = endX - startX;

    if (deltaX < -50) {
      setOpenSwipeId(id);
    } else if (deltaX > 35) {
      setOpenSwipeId((prev) => (prev === id ? null : prev));
    }
  };

  const markAsRead = (id) => {
    setReadIds((prev) => new Set(prev).add(id));
    setOpenSwipeId(null);
  };

  const deleteNotification = (id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
    setReadIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setOpenSwipeId(null);
  };

  /** First non-empty IMEI from loaded rows (API nests it under `device`). */
  const deviceImei = useMemo(() => {
    for (const n of notifications) {
      const imei = n?.device?.imei_number ?? n?.device?.imei;
      if (imei != null && String(imei).trim() !== "") return String(imei).trim();
    }
    return null;
  }, [notifications]);

  const normalizedForFilter = useMemo(() => {
    return notifications.map((item) => {
      const id = item.id;
      const isNew = item.status === "new" && !readIds.has(id);
      const created = item.created_at;
      const imei =
        item?.device?.imei_number != null
          ? String(item.device.imei_number).trim()
          : item?.device?.imei != null
            ? String(item.device.imei).trim()
            : "";
      return {
        raw: item,
        id,
        section: sectionLabelForDate(created),
        title: item.title || "Notification",
        message: item.message || "",
        type: item.type || "",
        severity: item.severity || "low",
        imei,
        time: formatRelativeTime(created),
        created,
        badge: isNew ? "New" : null,
        logo: logoFromItem(item),
        styles: severityStyles(item.severity),
      };
    });
  }, [notifications, readIds]);

  const filteredAlerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalizedForFilter;
    return normalizedForFilter.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.message.toLowerCase().includes(q) ||
        String(item.type).toLowerCase().includes(q) ||
        String(item.severity).toLowerCase().includes(q) ||
        (item.imei && item.imei.toLowerCase().includes(q))
    );
  }, [search, normalizedForFilter]);

  const groupedAlerts = useMemo(() => {
    return filteredAlerts.reduce((acc, item) => {
      const key = item.section || "Recent";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [filteredAlerts]);

  const loadMore = () => {
    if (page < lastPage && !loading) fetchPage(page + 1, true);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-2xl px-3 pb-6 pt-3 sm:px-4 sm:pt-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-foreground" />
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground">Notifications</h1>
              {deviceId && (
                <p className="text-xs text-muted-foreground max-w-[min(100%,28rem)]">
                  <span className="block truncate sm:whitespace-normal sm:break-words">
                    {vehicleLabel ? `${vehicleLabel} · ` : ""}
                    {deviceImei ? (
                      <>
                        <span className="text-muted-foreground">IMEI </span>
                        <span className="font-mono tabular-nums">{deviceImei}</span>
                        {" · "}
                      </>
                    ) : null}
                  </span>
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-foreground"
            aria-label="Close"
            onClick={() => navigate(-1)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {!deviceId && (
          <div className="mb-4 rounded-xl border border-dashed border-muted-foreground/20 bg-muted/30 p-4 text-center text-sm text-muted-foreground">
            Open alerts from a vehicle&apos;s map (Explore → Alerts) to see device notifications.
          </div>
        )}

        {loadError && deviceId && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{loadError}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => fetchPage(1, false)}>
              Retry
            </Button>
          </div>
        )}

        <div className="relative mb-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notifications"
            className="h-10 rounded-xl border-1 border-muted-foreground/20 bg-muted/50 pl-10 text-sm"
            disabled={!deviceId}
          />
        </div>

        {loading && notifications.length === 0 && deviceId && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading notifications…</p>
          </div>
        )}

        <div className="space-y-5">
          {Object.entries(groupedAlerts).map(([section, items]) => (
            <section key={section}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section}
              </h2>
              <div className="divide-y border-1 border-muted-foreground/20 rounded-xl bg-card">
                {items.map((item) => (
                  <div key={item.id} className="relative overflow-hidden">
                    <div className="absolute inset-y-0 right-0 z-0 flex items-center gap-2 px-2">
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 rounded-lg bg-emerald-500 px-3 text-xs font-semibold text-white hover:bg-emerald-500"
                        onClick={() => markAsRead(item.id)}
                      >
                        Read
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 rounded-lg bg-red-500 px-3 text-xs font-semibold text-white hover:bg-red-500"
                        onClick={() => deleteNotification(item.id)}
                      >
                        Dismiss
                      </Button>
                    </div>

                    <div
                      className={`relative z-10 flex items-center gap-3 bg-card p-3 transition-transform duration-200 ${
                        openSwipeId === item.id ? "-translate-x-[110px]" : "translate-x-0"
                      }`}
                      onTouchStart={(e) => onTouchStart(item.id, e)}
                      onTouchEnd={(e) => onTouchEnd(item.id, e)}
                      onClick={() => setOpenSwipeId((prev) => (prev === item.id ? null : prev))}
                    >
                      <div className="relative shrink-0">
                        <div
                          className={`flex h-11 w-11 items-center justify-center rounded-full text-xs font-bold ring-2 ${item.logo.className} ${item.styles.ring}`}
                        >
                          {item.logo.letter}
                        </div>
                        {item.badge && (
                          <span className="absolute -bottom-1 -right-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-semibold text-white">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">
                          <span className="font-semibold">{item.title}</span>
                          {item.message ? (
                            <span className="text-muted-foreground"> — {item.message}</span>
                          ) : null}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {[
                            item.type,
                            item.severity,
                            item.imei ? `IMEI ${item.imei}` : null,
                            item.time,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {deviceId && page < lastPage && notifications.length > 0 && (
          <div className="mt-4 flex justify-center ">
            <Button type="button" className="border-1 border-muted-foreground/20 text-foreground" variant="outline" size="sm" onClick={loadMore} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading…
                </>
              ) : (
                `Load more (${notifications.length} of ${total})`
              )}
            </Button>
          </div>
        )}

        {filteredAlerts.length === 0 && !loading && deviceId && !loadError && (
          <div className="mt-4 rounded-xl border border-dashed border-1 border-muted-foreground/20 p-6 text-center text-sm text-muted-foreground">
            No notifications found
          </div>
        )}
      </div>
    </div>
  );
}
